import React, { useState, useEffect } from 'react';
import CandleStickChart from '../stock/CandlestickChart';
import Drawer from '@mui/material/Drawer';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Link } from 'react-router-dom'
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Grid from '@mui/material/Grid';
import StickySettings from '../helpers/StickySettings.js';
import OHLCBarChart from '../components/OHLCBarChart.js'
import TradingViewChart from '../stock/TradingViewChart.js'
import tableHelpers from '../helpers/TableHelpers.js'
import StockQuickLookDrawer from '../stock/StockQuickLookDrawer.js'
import RichTable from '../components/richTable/RichTable.jsx'
import IndicatorSelector from '../stock/IndicatorSelector.js';

function Charts(props) {

    const [symbols, setSymbols] = useState([]);
    const [columns, setColumns] = useState(StickySettings.getNumber('chartColumns') ? StickySettings.getNumber('chartColumns') : 2);
    const [initialBars, setInitialBars] = useState(StickySettings.getNumber('chartInitialBars') ? StickySettings.getNumber('chartInitialBars') : 75);
    const [timeframe, setTimeframe] = useState(StickySettings.getNumber('chartTimeframe') ? StickySettings.getNumber('chartTimeframe') : 1);
    const [showVolumeOnChart, setShowVolumeOnChart] = useState(StickySettings.getBool('chartVolume', false));
    const [hideSymbolList, setHideSymbolList] = useState(true);
    const [allSymbols, setAllSymbols] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [allQuotes, setAllQuotes] = useState([]);
    const [updates, setUpdates] = useState(0);
    const [popupChartSymbol, setPopupChartSymbol] = useState(null);
    const [indicators, setIndicators] = useState([]);
    const [indicatorOptions, setIndicatorOptions] = useState([]);
    const [patterns, setPatterns] = useState([]);
    const smaIndicators = [
        { type: 'sma', id: 'sma20', location: 'upper', config: { period: 20 } },
        { type: 'sma', id: 'sma50', location: 'upper', config: { period: 50 } },
        { type: 'sma', id: 'sma200', location: 'upper', config: { period: 200 } }
    ];
    const [selectedIndicators, setSelectedIndicators] = useState(StickySettings.getJSON('chartIndicators', [...smaIndicators]));

    useEffect(() => {
        StickySettings.setJSON('chartIndicators', selectedIndicators);
    }, [selectedIndicators]);

    useEffect(() => {
        (async function () {
            const syms = await (await fetch(`/api/strategySymbols`)).json();
            setAllSymbols(syms.sort((a, b) => a.localeCompare(b)));

            let storedSyms = StickySettings.getString('chartSymbols');
            if (storedSyms) {
                setSymbols(storedSyms.split(','));
            }

            const query = `/api/stockIndicators?symbols=` + storedSyms;
            let d = await (await fetch(query)).json();
            console.log("indicators", d);
            setIndicators(d);

            const options = await (await fetch(`/api/indicators`)).json();
            setIndicatorOptions(options.indicators || []);
            setPatterns(options.patterns || []);
        })();
    }, []);

    useEffect(() => {
        (async function () {
            if (!hideSymbolList) {
                const quotes = await (await fetch(`/api/quote?symbols=${allSymbols.join(',')}`)).json();
                console.log("all quotes", quotes);
                setAllQuotes(quotes);
            }
        })();
    }, [hideSymbolList, allSymbols]);

    useEffect(() => {
        (async function () {
            const q = await (await fetch(`/api/quote?symbols=${symbols.join(',')}`)).json();
            console.log("quotes", q);
            setQuotes(q);
        })();
    }, [symbols, symbols.length, updates]);

    function addSymbol(sym) {
        setSymbols([...symbols, sym]);
        StickySettings.setString('chartSymbols', [...symbols, sym].join(','));
    }
    function removeSymbol(sym) {
        setSymbols(symbols.filter(s => s !== sym));
        StickySettings.setString('chartSymbols', symbols.filter(s => s !== sym).join(','));
    }
    function updateColumns(cols) {
        setColumns(cols);
        StickySettings.setNumber('chartColumns', Number(cols));
    }
    function updateInitialBars(bars) {
        if (bars === 52) {
            setInitialBars(52);
            setTimeframe(2);
            StickySettings.setNumber('chartInitialBars', 52);
            StickySettings.setNumber('chartTimeframe', 2);
        } else {
            setInitialBars(bars);
            setTimeframe(1);
            StickySettings.setNumber('chartInitialBars', Number(bars));
            StickySettings.setNumber('chartTimeframe', 1);
        }
    }

    function getQuoteForSymbol(symbol) {
        let quote = quotes.find(q => q.symbol === symbol);
        return quote ? quote : null;
    }

    function todayChangeFormatter(quote) {
        if (quote) {
            let highChange = (quote.high - quote.pclose) / quote.pclose * 100;
            let lowChange = (quote.low - quote.pclose) / quote.pclose * 100;
            let openChange = (quote.open - quote.pclose) / quote.pclose * 100;
            let change = quote.percentChange;

            return (
                <OHLCBarChart width="80px" hideLabels={true} high={highChange} low={lowChange} open={openChange} close={change} />
            );
        } else return "";
    }

    function getIndicatorForSymbol(symbol) {
        let indicator = indicators.find(i => i.symbol === symbol);
        return indicator ? indicator : null;
    }

    async function showSymbolList() {
        setHideSymbolList(false);
    }

    function getAllSymbols() {
        return allSymbols.map(s => {
            let quote = allQuotes.find(q => q.symbol === s);
            return {
                symbol: s,
                quote: quote
            }
        });
    }

    function macdFormatter(cell, row) {
        if (row.macd > row.macdSignal) return <>⬆️</>
        else return <>⬇️</>
    }

    function trendFormatter(cell, row) {
        if (row.above50sma && row.upward50sma) {
            return <>Up</>
        } else if (row.above50sma && !row.upward50sma) {
            return <>Recovering</>
        } else if (!row.above50sma && row.upward50sma) {
            return <>Falling</>
        } else if (!row.above50sma && !row.upward50sma) {
            return <>Down</>
        } else {
            return <>None</>
        }
    }

    let width = window.innerWidth / columns - 50;
    let height = Math.min(window.innerHeight - 300, width * .6);

    return (
        <div style={{ padding: '20px' }}>
            <Link onClick={() => setUpdates(updates + 1)}>🔄 Reload</Link>
            <br></br>
            Columns: <ToggleButtonGroup
                value={columns}
                exclusive
                onChange={(e) => updateColumns(parseInt(e.target.value))}
            >
                <ToggleButton value={1}>1</ToggleButton>
                <ToggleButton value={2}>2</ToggleButton>
                <ToggleButton value={3}>3</ToggleButton>
                <ToggleButton value={4}>4</ToggleButton>
            </ToggleButtonGroup>
            &nbsp;&nbsp;<input type="checkbox" checked={showVolumeOnChart} onChange={(e) => {
                setShowVolumeOnChart(e.target.checked);
                StickySettings.setBool('chartVolume', e.target.checked);
            }} /> Show Volume
            <br></br>
            <IndicatorSelector indicators={indicatorOptions} selectedIndicators={selectedIndicators} setSelectedIndicators={setSelectedIndicators} patterns={patterns} />
            <br></br>

            <div style={{ backgroundColor: 'white' }} className='chartduration-button'>
                <ToggleButtonGroup
                    value={initialBars}
                    exclusive
                    color="primary"
                    size="large"
                    onChange={(e) => updateInitialBars(parseInt(e.target.value))}
                >
                    <ToggleButton value={22}>1M</ToggleButton>
                    <ToggleButton value={66}>3M</ToggleButton>
                    <ToggleButton value={125}>6M</ToggleButton>
                    <ToggleButton value={252}>1Y</ToggleButton>
                    <ToggleButton value={52}>Weekly</ToggleButton>
                </ToggleButtonGroup>
            </div>
            <br></br>
            <br></br>

            <Drawer open={!hideSymbolList} onClose={() => setHideSymbolList(true)}>
                <div style={{ padding: "20px" }}>
                    <RichTable data={getAllSymbols()} mappers={[
                        {
                            title: `Include`, field: 'symbol', formatter: (symbol, row) => {
                                return (
                                    <input
                                        type="checkbox"
                                        checked={symbols.includes(symbol)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                addSymbol(symbol);
                                            } else {
                                                removeSymbol(symbol);
                                            }
                                        }}
                                    />

                                )
                            }
                        },
                        { title: `Symbol`, field: 'symbol' },
                        { title: `Today`, field: 'quote', formatter: todayChangeFormatter },
                    ]} className="table-striped table-hover table-condensed" />
                </div>
            </Drawer>

            <Drawer
                anchor={'bottom'}
                open={popupChartSymbol !== null}
                onClose={() => setPopupChartSymbol(null)}
            >
                <center>
                    <TradingViewChart symbol={popupChartSymbol} />
                </center>
            </Drawer>

            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                {symbols.map(symbol => (
                    <div key={symbol} style={{ marginBottom: '40px' }}>
                        <div class='sameline'>
                            <h4 style={{ display: 'flex' }}>
                                {getQuoteForSymbol(symbol) ?
                                    <><StockQuickLookDrawer symbol={symbol} planId={props.planId} userId={props.userId} accountId={props.accountId} />
                                        &nbsp;{todayChangeFormatter(getQuoteForSymbol(symbol))}
                                        &nbsp;<Link onClick={() => setPopupChartSymbol(symbol)}> 📈</Link>
                                        <div style={{ fontSize: 'small' }}>{getQuoteForSymbol(symbol) && getQuoteForSymbol(symbol).postMarketPercentChange ? <>&nbsp;Ext Hours: {tableHelpers.percentChangeFormatter(getQuoteForSymbol(symbol)?.postMarketPercentChange)}</> : null}</div>
                                    </>
                                    : null}
                            </h4>
                        </div>
                        {getIndicatorForSymbol(symbol) ?
                            <div style={{ width: width - 50 }}>
                                <RichTable data={[getIndicatorForSymbol(symbol)]} mappers={[
                                    { title: `Drawdown`, field: 'drawdown', formatter: tableHelpers.percentFormatter },
                                    { title: `20 BB`, field: 'stdDevFrom20Sma', formatter: tableHelpers.signedDecimalFormatter },
                                    { title: `Volatility`, field: 'relativeVolatility', formatter: tableHelpers.percentChangeFormatterNoDecimal },
                                    { title: `Cycle`, field: '', formatter: trendFormatter },
                                    { title: `Momentum`, field: 'macd', formatter: macdFormatter },
                                    { title: `ADX`, field: 'adx', formatter: tableHelpers.decimalFormatter },
                                    { title: `Chop`, field: 'chopCount', formatter: tableHelpers.intFormatter },

                                ]} className="table-striped table-hover table-condensed table-bordered"
                                />
                            </div>
                            :
                            null}
                        <CandleStickChart indicators={selectedIndicators} updates={updates} timeframe={timeframe} showVolume={showVolumeOnChart} hideZoomButtons={true} hideTooltip={true} initialVisibleBars={initialBars} width={width} height={height} showMovingAverage={false} symbol={symbol} showDate={props.showDate} />
                    </div>
                ))}
            </Grid>

            <div className='addchart-button'>
                <Link onClick={() => showSymbolList()}>
                    <AddCircleIcon sx={{ fontSize: 40 }} />
                </Link>
            </div>
        </div>
    );
}

export default Charts;